import React, { Component } from "react";
import axios from "axios";
import Helmet from "react-helmet";
import { Form, Modal } from "react-bootstrap";
import Layout from "../../Layout/layout";
import { apiHost } from "../../api/apiClient";
import NewsContent, { formatNewsDate, getNewsPreview, getNewsReleaseDate } from "./newsContent";
import "../../styles/news/news.scss";

const emptyForm = {
  id: null,
  draftAudience: "UAT",
  title: "",
  summary: "",
  contentType: "Message",
  messageText: "",
  imageDataUrl: "",
  htmlContent: "",
  displayMode: "Once",
  displayStartLocal: "",
  displayEndLocal: "",
};

const imageTypes = ["image/png", "image/jpeg"];

function toLocalDateTimeInput(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
}

function toUtcIso(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function getDisplayModeLabel(item) {
  if (item && item.displayMode === "Always") {
    return "Every login · Until deactivated";
  }

  if (item && item.displayMode === "DateRange") {
    return "Every login · Date range";
  }

  return "Once per user";
}

class NewsAdmin extends Component {
  constructor(props) {
    super(props);
    const mode = props.match && props.match.path && props.match.path.indexOf("/edit/") !== -1 ? "edit" : props.match && props.match.path && props.match.path.indexOf("/create") !== -1 ? "create" : "list";
    this.state = {
      mode,
      items: [],
      form: { ...emptyForm },
      previewItem: null,
      readReceiptItem: null,
      readReceipts: [],
      expandedGroups: {},
      environment: null,
      draftDistributionLocked: false,
      originalDraftAudience: "UAT",
      isLoading: true,
      isSaving: false,
      isDraggingImage: false,
      isLoadingReads: false,
      error: "",
      success: "",
    };
  }

  componentDidMount() {
    axios.get(`${apiHost}/api/News/Admin/Environment`, { headers: this.getAuthHeaders() })
      .then(response => this.setState({ environment: response.data.environment }))
      .catch(() => this.setState({ error: "Unable to confirm the NEWS environment. Refresh before saving." }));
    if (this.state.mode === "list") {
      this.loadNews();
    } else if (this.state.mode === "edit") {
      this.loadEditNews();
    } else {
      this.setState({ isLoading: false });
    }
  }

  getAuthHeaders() {
    return {
      Authorization: "Bearer " + localStorage.getItem("token"),
    };
  }

  loadNews = async () => {
    this.setState({ isLoading: true, error: "", success: "" });

    try {
      const response = await axios.get(`${apiHost}/api/News/Admin`, {
        headers: this.getAuthHeaders(),
      });

      this.setState({
        items: Array.isArray(response.data) ? response.data : [],
        isLoading: false,
      });
    } catch (error) {
      this.setState({
        items: [],
        isLoading: false,
        error: "Unable to load NEWS communications.",
      });
    }
  };

  loadEditNews = async () => {
    this.setState({ isLoading: true, error: "" });

    try {
      const response = await axios.get(`${apiHost}/api/News/Admin/${this.props.match.params.id}`, {
        headers: this.getAuthHeaders(),
      });
      const item = response.data || {};

      this.setState({
        form: {
          id: item.id,
          draftAudience: item.draftAudience || "UAT",
          title: item.title || "",
          summary: item.summary || "",
          contentType: item.contentType || "Message",
          messageText: item.messageText || "",
          imageDataUrl: item.imageDataUrl || "",
          htmlContent: item.htmlContent || "",
          displayMode: item.displayMode || "Once",
          displayStartLocal: toLocalDateTimeInput(item.displayStartUtc),
          displayEndLocal: toLocalDateTimeInput(item.displayEndUtc),
        },
        environment: item.environmentKey || this.state.environment,
        draftDistributionLocked: !!item.draftDistributionLocked,
        originalDraftAudience: item.draftAudience || "UAT",
        isLoading: false,
      });
    } catch (error) {
      this.setState({ isLoading: false, error: "Unable to load communication." });
    }
  };

  goToCreate = () => {
    this.props.history.push("/news-admin/create");
  };

  goToList = () => {
    this.props.history.push("/news-admin");
  };

  isEditLocked = (item) => item.environmentKey === "UAT" && item.draftAudience === "Both" && !!item.draftDistributionLocked;

  goToEdit = (item) => {
    if (this.isEditLocked(item)) return;
    this.props.history.push(`/news-admin/edit/${item.id}`);
  };

  handleFieldChange = (field, value) => {
    this.setState((state) => ({
      form: {
        ...state.form,
        [field]: value,
      },
    }));
  };

  handleImageFile = (file) => {
    if (!file) {
      return;
    }

    if (imageTypes.indexOf(file.type) === -1) {
      this.setState({ error: "Please upload a PNG or JPEG file.", isDraggingImage: false });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.handleFieldChange("imageDataUrl", reader.result);
      this.setState({ error: "", isDraggingImage: false });
    };
    reader.readAsDataURL(file);
  };

  handleImageUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    this.handleImageFile(file);
  };

  handleImageDragOver = (event) => {
    event.preventDefault();
    this.setState({ isDraggingImage: true });
  };

  handleImageDragLeave = (event) => {
    event.preventDefault();
    this.setState({ isDraggingImage: false });
  };

  handleImageDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
    this.handleImageFile(file);
  };

  getPayload() {
    const form = this.state.form;
    return {
      id: form.id,
      draftAudience: this.state.environment === "UAT" ? form.draftAudience : undefined,
      title: form.title,
      summary: form.summary,
      contentType: form.contentType,
      messageText: form.contentType === "Message" ? form.messageText : "",
      imageDataUrl: form.contentType === "Image" ? form.imageDataUrl : "",
      htmlContent: form.contentType === "Html" ? form.htmlContent : "",
      displayMode: form.displayMode,
      displayStartUtc: form.displayMode === "DateRange" ? toUtcIso(form.displayStartLocal) : null,
      displayEndUtc: form.displayMode === "DateRange" ? toUtcIso(form.displayEndLocal) : null,
    };
  }

  saveNews = async (event) => {
    event.preventDefault();
    if (!this.state.environment) return;
    if (this.state.originalDraftAudience === "Both" && this.state.form.draftAudience === "UAT"
        && !window.confirm("Saving UAT only will delete the Production copy, including its versions and read receipts. Continue?")) return;
    this.setState({ isSaving: true, error: "", success: "" });

    try {
      await axios.post(`${apiHost}/api/News/Admin`, this.getPayload(), {
        headers: this.getAuthHeaders(),
      });

      this.props.history.push("/news-admin");
    } catch (error) {
      const message = error && error.response && error.response.data ? error.response.data : "Unable to save communication.";
      this.setState({ isSaving: false, error: message });
    }
  };

  setActive = async (item) => {
    this.setState({ error: "", success: "" });

    try {
      await axios.post(`${apiHost}/api/News/Admin/${item.id}/SetActive`, {}, {
        headers: this.getAuthHeaders(),
      });
      this.setState({ success: "Communication set active and published." });
      await this.loadNews();
    } catch (error) {
      this.setState({ error: "Unable to publish communication." });
    }
  };

  deactivate = async (item) => {
    this.setState({ error: "", success: "" });

    try {
      await axios.post(`${apiHost}/api/News/Admin/${item.id}/Deactivate`, {}, {
        headers: this.getAuthHeaders(),
      });
      this.setState({ success: "Communication deactivated." });
      await this.loadNews();
    } catch (error) {
      this.setState({ error: "Unable to deactivate communication." });
    }
  };

  deleteNews = async (item) => {
    if (!window.confirm(`Delete "${item.title}" and all of its versions?`)) {
      return;
    }

    this.setState({ error: "", success: "" });

    try {
      await axios.delete(`${apiHost}/api/News/Admin/${item.id}`, {
        headers: this.getAuthHeaders(),
      });
      this.setState({ success: "Communication deleted." });
      await this.loadNews();
    } catch (error) {
      this.setState({ error: "Unable to delete communication." });
    }
  };

  previewNews = async (item) => {
    if (!item.id) {
      this.setState({ previewItem: item });
      return;
    }

    this.setState({ previewItem: item, error: "" });

    try {
      const response = await axios.get(`${apiHost}/api/News/Admin/${item.id}`, {
        headers: this.getAuthHeaders(),
      });
      this.setState({ previewItem: response.data || item });
    } catch (error) {
      this.setState({ error: "Unable to load communication preview." });
    }
  };

  showReadReceipts = async (item) => {
    this.setState({ readReceiptItem: item, readReceipts: [], isLoadingReads: true, error: "" });

    try {
      const response = await axios.get(`${apiHost}/api/News/Admin/${item.id}/Reads`, {
        headers: this.getAuthHeaders(),
      });
      this.setState({
        readReceipts: Array.isArray(response.data) ? response.data : [],
        isLoadingReads: false,
      });
    } catch (error) {
      this.setState({ isLoadingReads: false, error: "Unable to load read receipts." });
    }
  };

  resetUserRead = async (receipt) => {
    const item = this.state.readReceiptItem;
    if (!item || !receipt || !window.confirm(`Show this communication to ${receipt.email} again on their next login?`)) {
      return;
    }

    try {
      await axios.delete(`${apiHost}/api/News/Admin/${item.id}/Reads/${encodeURIComponent(receipt.userId)}`, {
        headers: this.getAuthHeaders(),
      });
      await this.showReadReceipts(item);
      await this.loadNews();
      this.setState({ success: `Read flag reset for ${receipt.email}.` });
    } catch (error) {
      this.setState({ error: "Unable to reset this user's read flag." });
    }
  };

  resetAllReads = async () => {
    const item = this.state.readReceiptItem;
    if (!item || !window.confirm(`Reset all read flags for "${item.title}"? Every user will be eligible to see it again.`)) {
      return;
    }

    try {
      await axios.delete(`${apiHost}/api/News/Admin/${item.id}/Reads`, {
        headers: this.getAuthHeaders(),
      });
      await this.showReadReceipts(item);
      await this.loadNews();
      this.setState({ success: "All read flags reset." });
    } catch (error) {
      this.setState({ error: "Unable to reset read flags." });
    }
  };

  getGroupedItems() {
    const groups = {};

    this.state.items.forEach((item) => {
      const key = item.communicationId || item.id;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
    });

    return Object.keys(groups)
      .map((key) => {
        const versions = groups[key].sort((a, b) => (b.version || 0) - (a.version || 0) || b.id - a.id);
        const active = versions.find((item) => item.isActive);
        return {
          key,
          active,
          latest: versions[0],
          display: active || versions[0],
          versions,
        };
      })
      .sort((a, b) => {
        if (!!a.active !== !!b.active) {
          return a.active ? -1 : 1;
        }

        const aTime = new Date(getNewsReleaseDate(a.display) || a.display.updatedUtc || a.display.createdUtc || 0).getTime();
        const bTime = new Date(getNewsReleaseDate(b.display) || b.display.updatedUtc || b.display.createdUtc || 0).getTime();
        return bTime - aTime;
      });
  }

  toggleGroup = (groupKey) => {
    this.setState((state) => ({
      expandedGroups: {
        ...state.expandedGroups,
        [groupKey]: !state.expandedGroups[groupKey],
      },
    }));
  };

  renderContentInput() {
    const form = this.state.form;

    if (form.contentType === "Image") {
      return (
        <Form.Group controlId="newsImage">
          <Form.Label>Image</Form.Label>
          <div
            className={this.state.isDraggingImage ? "news-image-dropzone dragging" : "news-image-dropzone"}
            onDragLeave={this.handleImageDragLeave}
            onDragOver={this.handleImageDragOver}
            onDrop={this.handleImageDrop}
          >
            <i className="fa fa-cloud-upload"></i>
            <strong>Drop PNG or JPEG here</strong>
            <span>or choose a file from your computer</span>
            <Form.Control accept="image/png,image/jpeg" onChange={this.handleImageUpload} type="file" />
          </div>
          {form.imageDataUrl && (
            <div className="news-admin-image-preview">
              <img src={form.imageDataUrl} alt="Selected NEWS upload" />
            </div>
          )}
        </Form.Group>
      );
    }

    if (form.contentType === "Html") {
      return (
        <Form.Group controlId="newsHtml">
          <Form.Label>HTML code</Form.Label>
          <Form.Control
            as="textarea"
            onChange={(event) => this.handleFieldChange("htmlContent", event.target.value)}
            rows={14}
            value={form.htmlContent}
          />
        </Form.Group>
      );
    }

    return (
      <Form.Group controlId="newsMessage">
        <Form.Label>Message</Form.Label>
        <Form.Control
          as="textarea"
          onChange={(event) => this.handleFieldChange("messageText", event.target.value)}
          rows={12}
          value={form.messageText}
        />
      </Form.Group>
    );
  }

  renderHeader(title, subtitle, action) {
    return (
      <div className="news-report-header">
        <div>
          <div className="news-report-eyebrow">Admin Communications</div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        {action && <div className="news-report-actions">{action}</div>}
      </div>
    );
  }

  renderFormPage() {
    const isEditing = this.state.mode === "edit";
    const title = isEditing ? "Edit Communication" : "Create Communication";
    const subtitle = isEditing ? "Display-only changes apply immediately to this version, including while live. Content changes create a new draft that must be published." : "Prepare a communication, then publish it from the NEWS Admin list.";

    return (
      <Layout>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <div className="news-report-page">
          <div className="news-report-shell">
            {this.renderHeader(title, subtitle, (
              <button className="news-report-button news-report-button-muted" onClick={this.goToList} type="button">
                Back to NEWS Admin
              </button>
            ))}

            {this.state.error && <div className="news-report-status news-report-status-error">{this.state.error}</div>}

            <Form className="news-admin-form news-admin-form-page" onSubmit={this.saveNews}>
              {this.state.environment === "UAT" && (
                <Form.Group controlId="newsDraftAudience">
                  <Form.Label>Prepare for</Form.Label>
                  <Form.Control as="select" value={this.state.form.draftAudience}
                    disabled={this.state.draftDistributionLocked || this.state.isSaving}
                    onChange={event => this.handleFieldChange("draftAudience", event.target.value)}>
                    <option value="UAT">UAT only</option>
                    <option value="Both">Both — UAT and Production</option>
                  </Form.Control>
                  <Form.Text className="text-muted">
                    {this.state.draftDistributionLocked
                      ? "Deactivate this communication in both environments before switching or saving to Both."
                      : this.state.originalDraftAudience === "Both" && this.state.form.draftAudience === "UAT"
                        ? "Saving will delete the inactive Production copy. The UAT communication stays here."
                        : this.state.form.draftAudience === "Both"
                          ? "Save updates both copies while inactive. Production stays a draft until activated there. Deleting UAT later leaves Production intact."
                          : "This communication is saved in UAT only."}
                  </Form.Text>
                </Form.Group>
              )}
              <Form.Group controlId="newsTitle">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  onChange={(event) => this.handleFieldChange("title", event.target.value)}
                  required
                  value={this.state.form.title}
                />
              </Form.Group>

              <Form.Group controlId="newsSummary">
                <Form.Label>Tile preview</Form.Label>
                <Form.Control
                  as="textarea"
                  onChange={(event) => this.handleFieldChange("summary", event.target.value)}
                  rows={3}
                  value={this.state.form.summary}
                />
              </Form.Group>

              <Form.Group controlId="newsContentType">
                <Form.Label>Type</Form.Label>
                <Form.Control
                  as="select"
                  onChange={(event) => this.handleFieldChange("contentType", event.target.value)}
                  value={this.state.form.contentType}
                >
                  <option value="Message">Customised Message</option>
                  <option value="Image">Image (PNG or JPEG)</option>
                  <option value="Html">HTML code</option>
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="newsDisplayMode">
                <Form.Label>Display behaviour</Form.Label>
                <Form.Control
                  as="select"
                  onChange={(event) => this.handleFieldChange("displayMode", event.target.value)}
                  value={this.state.form.displayMode}
                >
                  <option value="Once">Show once per user</option>
                  <option value="DateRange">Show on every login during a date range</option>
                  <option value="Always">Show on every login until manually deactivated</option>
                </Form.Control>
                <Form.Text className="text-muted">
                  Closing the modal still records a read receipt. Persistent modes continue showing regardless of that receipt.
                </Form.Text>
              </Form.Group>

              {this.state.form.displayMode === "DateRange" && (
                <div className="news-admin-date-range">
                  <Form.Group controlId="newsDisplayStart">
                    <Form.Label>Starts</Form.Label>
                    <Form.Control
                      onChange={(event) => this.handleFieldChange("displayStartLocal", event.target.value)}
                      required
                      type="datetime-local"
                      value={this.state.form.displayStartLocal}
                    />
                  </Form.Group>
                  <Form.Group controlId="newsDisplayEnd">
                    <Form.Label>Expires</Form.Label>
                    <Form.Control
                      onChange={(event) => this.handleFieldChange("displayEndLocal", event.target.value)}
                      required
                      type="datetime-local"
                      value={this.state.form.displayEndLocal}
                    />
                  </Form.Group>
                </div>
              )}

              {this.renderContentInput()}

              <div className="news-admin-form-actions">
                <button className="news-report-button news-report-button-secondary news-admin-cancel" disabled={this.state.isSaving} onClick={this.goToList} type="button">
                  Cancel
                </button>
                <button className="news-report-button news-report-button-primary" disabled={this.state.isSaving || !this.state.environment || (this.state.environment === "UAT" && this.state.form.draftAudience === "Both" && this.state.draftDistributionLocked)} type="submit">
                  {this.state.isSaving ? "Saving..." : isEditing ? "Save Changes" : "Create"}
                </button>
                <button className="news-report-button news-report-button-secondary" onClick={() => this.previewNews(this.getPayload())} type="button">
                  Preview
                </button>
              </div>
            </Form>
          </div>
        </div>
        {this.renderPreviewModal()}
      </Layout>
    );
  }

  renderVersionRow(item) {
    return (
      <div className="news-version-row" key={item.id}>
        <div>
          <strong>Version {item.version}</strong>
          <span>{item.isPublished ? "Published" : "Draft"}</span>
          {item.isActive && <span className="active">Active</span>}
          <small>{formatNewsDate(getNewsReleaseDate(item) || item.updatedUtc || item.createdUtc)}</small>
        </div>
        <div className="news-version-actions">
          <button className="news-icon-button" onClick={() => this.previewNews(item)} type="button" title="Preview">
            <i className="fa fa-eye"></i>
          </button>
          <button className="news-icon-button" onClick={() => this.goToEdit(item)} type="button" disabled={this.isEditLocked(item)} title={this.isEditLocked(item) ? "Deactivate this communication in UAT and Production before editing." : "Edit this version"}>
            <i className="fa fa-pencil"></i>
          </button>
          {!item.isActive && (
            <button className="news-icon-button news-icon-button-primary" onClick={() => this.setActive(item)} type="button" title="Set active and publish">
              <i className="fa fa-bullhorn"></i>
            </button>
          )}
          <button className="news-icon-button" onClick={() => this.showReadReceipts(item)} type="button" title="View readers">
            <i className="fa fa-users"></i>
            <span className="news-icon-count">{item.readCount || 0}</span>
          </button>
        </div>
      </div>
    );
  }

  renderGroup(group) {
    const item = group.display;
    const isExpanded = !!this.state.expandedGroups[group.key];
    const hasPendingLatestDraft = group.latest.id !== item.id && !group.latest.isPublished;

    return (
      <div className={item.isActive ? "news-admin-row active" : "news-admin-row"} key={group.key}>
        <div className="news-admin-row-main">
          <div className="news-admin-status-line">
            <span>{item.contentType}</span>
            <span>Latest v{item.version}</span>
            {item.environmentKey === "UAT" && <span>{item.draftAudience === "Both" ? "Both · Production copy" : "UAT only"}</span>}
            {item.isPublished ? <span>Published</span> : <span>Draft</span>}
            <span>{getDisplayModeLabel(item)}</span>
            {item.isActive && <strong>Active</strong>}
            {hasPendingLatestDraft && <span>Draft v{group.latest.version} pending</span>}
          </div>
          {this.isEditLocked(item) && <p className="news-edit-lock-note">Editing is locked while either copy is active. Deactivate this communication in UAT and Production before editing.</p>}
          <h3>{item.title}</h3>
          <p>{getNewsPreview(item)}</p>
          <div className="news-admin-date">{formatNewsDate(getNewsReleaseDate(item) || item.updatedUtc || item.createdUtc)}</div>
          {group.versions.length > 1 && (
            <button className="news-version-toggle" onClick={() => this.toggleGroup(group.key)} type="button">
              {isExpanded ? "Hide versions" : `Show ${group.versions.length} versions`}
            </button>
          )}
        </div>
        <div className="news-admin-row-actions">
          <button className="news-report-button news-report-button-muted" onClick={() => this.previewNews(item)} type="button">
            Preview
          </button>
          <button className="news-report-button news-report-button-secondary" onClick={() => this.goToEdit(item)} type="button" disabled={this.isEditLocked(item)} title={this.isEditLocked(item) ? "Deactivate this communication in UAT and Production before editing." : "Edit"}>
            Edit
          </button>
          <button className="news-report-button news-report-button-muted" onClick={() => this.showReadReceipts(item)} type="button">
            Read by ({item.readCount || 0})
          </button>
          {item.isActive ? (
            <button className="news-report-button news-report-button-warning" onClick={() => this.deactivate(item)} type="button">
              Deactivate
            </button>
          ) : (
            <button className="news-report-button news-report-button-primary" onClick={() => this.setActive(item)} type="button">
              Set Active/Publish
            </button>
          )}
          <button className="news-report-button news-report-button-danger" onClick={() => this.deleteNews(item)} type="button">
            Delete
          </button>
        </div>
        {isExpanded && <div className="news-version-list">{group.versions.map((version) => this.renderVersionRow(version))}</div>}
      </div>
    );
  }

  renderListPage() {
    const groups = this.getGroupedItems();

    return (
      <Layout>
        <Helmet>
          <title>NEWS Admin</title>
        </Helmet>
        <div className="news-report-page">
          <div className="news-report-shell">
            {this.renderHeader("NEWS Admin", "Create, edit and publish customer communications.", (
              <button className="news-report-button news-report-button-primary" onClick={this.goToCreate} type="button">
                <i className="fa fa-plus"></i>
                Create new
              </button>
            ))}

            {this.state.error && <div className="news-report-status news-report-status-error">{this.state.error}</div>}
            {this.state.success && <div className="news-report-status news-report-status-info">{this.state.success}</div>}

            <div className="news-admin-list-shell">
              {this.state.isLoading && <div className="news-empty">Loading communications...</div>}
              {!this.state.isLoading && groups.length === 0 && <div className="news-empty">No communications created yet.</div>}
              {groups.map((group) => this.renderGroup(group))}
            </div>
          </div>
        </div>
        {this.renderPreviewModal()}
        {this.renderReadReceiptsModal()}
      </Layout>
    );
  }

  renderPreviewModal() {
    return (
      <Modal
        className={this.state.previewItem && this.state.previewItem.contentType === "Html" ? "news-modal news-modal-html" : "news-modal"}
        show={!!this.state.previewItem}
        onHide={() => this.setState({ previewItem: null })}
        centered
        size={this.state.previewItem && this.state.previewItem.contentType === "Html" ? "xl" : "lg"}
      >
        <Modal.Header closeButton>
          <Modal.Title>{this.state.previewItem && this.state.previewItem.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NewsContent item={this.state.previewItem} />
        </Modal.Body>
        <Modal.Footer>
          <button className="news-report-button news-report-button-primary" onClick={() => this.setState({ previewItem: null })} type="button">
            Close
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  renderReadReceiptsModal() {
    const readCount = this.state.isLoadingReads && this.state.readReceiptItem
      ? this.state.readReceiptItem.readCount || 0
      : this.state.readReceipts.length;

    return (
      <Modal className="news-modal" show={!!this.state.readReceiptItem} onHide={() => this.setState({ readReceiptItem: null })} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{this.state.readReceiptItem ? `Read by (${readCount}): ${this.state.readReceiptItem.title}` : "Read by"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.state.isLoadingReads && <div className="news-empty">Loading readers...</div>}
          {!this.state.isLoadingReads && this.state.readReceipts.length === 0 && (
            <div className="news-empty">No users have read this communication yet.</div>
          )}
          {!this.state.isLoadingReads && this.state.readReceipts.length > 0 && (
            <div className="news-read-table">
              <div className="news-read-table-header">
                <span>Email address</span>
                <span>Read timestamp</span>
                <span>Action</span>
              </div>
              {this.state.readReceipts.map((receipt, index) => (
                <div className="news-read-table-row" key={`${receipt.userId}-${receipt.readUtc}-${index}`}>
                  <span>{receipt.email}</span>
                  <span>{formatNewsDate(receipt.readUtc)}</span>
                  <span>
                    {this.state.readReceiptItem && (this.state.readReceiptItem.displayMode || "Once") === "Once" && (
                      <button className="news-read-reset" onClick={() => this.resetUserRead(receipt)} type="button">
                        Reset
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {this.state.readReceipts.length > 0 && this.state.readReceiptItem && (this.state.readReceiptItem.displayMode || "Once") === "Once" && (
            <button className="news-report-button news-report-button-warning" onClick={this.resetAllReads} type="button">
              Reset all reads
            </button>
          )}
          <button className="news-report-button news-report-button-primary" onClick={() => this.setState({ readReceiptItem: null })} type="button">
            Close
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    if (this.state.mode !== "list") {
      return this.renderFormPage();
    }

    return this.renderListPage();
  }
}

export default NewsAdmin;
